import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  Typography,
  CircularProgress,
  Chip,
  Rating,
} from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { getBestSellingProducts } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";
import { getGradientText, getCardHover } from "../../../../../theme/luxuryTheme";

const BestSellersPage = () => {
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
    useEffect(() => {
      document.title = 'DIDA MAX — Best Seller';
    }, []);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getBestSellingProducts();
        // دعم أشكال الاستجابة: { success, data: [...] } أو مصفوفة مباشرة
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        setProducts(list);
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getName = (p) => (lang === "ar" ? p.name_ar || p.name_en : p.name_en || p.name_ar);
  const getImage = (p) => getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "");
  // helper: pick price entry by stored country_id
  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfo = (product) => {
    if (!product) return null;
    const countryId = getSelectedCountryId();
    const curr = product.currencies || product.Currencies || [];
    let entry = null;
    if (countryId) entry = curr.find((c) => String(c.country_id) === String(countryId));
    if (!entry && curr.length) entry = curr[0];
    if (!entry) {
      const fallback = product.price || product.price_after_discount || null;
      return fallback ? { price: String(product.price || ""), price_after_discount: product.price_after_discount ? String(product.price_after_discount) : null } : null;
    }
    const rawPrice = entry.price != null ? String(entry.price) : null;
    const rawDiscount =
      entry.price_after_discount != null
        ? String(entry.price_after_discount)
        : null;

    const priceNum = parseFloat(rawPrice || "");
    const discountNum = parseFloat(rawDiscount || "");
    const hasValidDiscount =
      Number.isFinite(discountNum) &&
      discountNum > 0 &&
      (!Number.isFinite(priceNum) || discountNum < priceNum);

    return {
      price: rawPrice,
      price_after_discount: hasValidDiscount ? rawDiscount : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  if (loading) {
    return (
      <Box 
        sx={{ 
          py: 8, 
          display: "flex", 
          justifyContent: "center",
          minHeight: "60vh",
          alignItems: "center",
          background: (theme) => theme.palette.background.gradient
        }}
      >
        <CircularProgress size={60} thickness={4} color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8, textAlign: "center", minHeight: "40vh" }}>
        <Typography color="error" variant="h6" sx={{ fontWeight: 500 }}>
          {t("error_loading_best_sellers", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل الأكثر مبيعاً" : "Error loading best sellers" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        py: 6, 
        px: { xs: 2, md: 4 },
        background: (theme) => theme.palette.background.gradient,
        minHeight: "70vh"
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            mb: 5,
            pb: 3,
            borderBottom: "3px solid",
            borderImage: "linear-gradient(90deg, #d4af37 0%, #f4e5c2 50%, #d4af37 100%) 1"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                background: (theme) => theme.palette.gradient.fire,
                borderRadius: "50%",
                p: 1.5,
                display: "flex",
                boxShadow: (theme) => theme.shadows[19]
              }}
            >
              <LocalFireDepartmentIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography 
              variant="h3" 
              fontWeight="800" 
              sx={getGradientText("dark")}
            >
              {t("best_sellers_title", { defaultValue: lang === "ar" ? "الأكثر مبيعاً" : "Best Sellers" })}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {products.map((product) => {
            const pinfo = getPriceInfo(product);
            const originalNum = parseFloat(pinfo?.price || "");
            const discountedNum = parseFloat(pinfo?.price_after_discount || "");
            const hasDiscount =
              !!pinfo?.price_after_discount &&
              Number.isFinite(originalNum) &&
              Number.isFinite(discountedNum) &&
              discountedNum > 0 &&
              discountedNum < originalNum;
            const discountPercent = hasDiscount
              ? Math.round(((originalNum - discountedNum) / originalNum) * 100)
              : 0;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card 
                  sx={{ 
                    height: "100%",
                    position: "relative",
                    overflow: "visible",
                    background: "#fff",
                    ...getCardHover()
                  }}
                >
                  {hasDiscount && (
                    <Chip
                      icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
                      label={`-${discountPercent}%`}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: lang === "ar" ? "auto" : 16,
                        left: lang === "ar" ? 16 : "auto",
                        zIndex: 2,
                        background: (theme) => theme.palette.gradient.red,
                        color: "#fff",
                        fontWeight: "800",
                        fontSize: "0.85rem",
                        boxShadow: (theme) => theme.shadows[15],
                        border: "2px solid #fff"
                      }}
                    />
                  )}

                  <CardActionArea 
                    component={Link} 
                    to={`/product/${product.id}`} 
                    sx={{ 
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      p: 3
                    }}
                  >
                    {/* Product Image */}
                    <Box 
                      sx={{ 
                        width: "100%",
                        height: 240,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        overflow: "hidden",
                        borderRadius: 2,
                        background: "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)",
                        position: "relative"
                      }}
                    >
                      <Box 
                        component="img" 
                        src={getImage(product)} 
                        alt={getName(product)} 
                        className="product-image"
                        sx={{ 
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                        }} 
                      />
                    </Box>

                    {/* Product Info */}
                    <Box sx={{ flex: 1, textAlign: "center", width: "100%" }}>
                      <Typography 
                        variant="h6" 
                        fontWeight="700" 
                        sx={{
                          mb: 2,
                          minHeight: 56,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          color: "secondary.main",
                          lineHeight: 1.4,
                          fontSize: "1.1rem"
                        }}
                      >
                        {getName(product)}
                      </Typography>

                      {/* Price Section */}
                      <Box sx={{ mb: 2 }}>
                        {(() => {
                          if (!pinfo) return null;
                          const orig = formatPrice(pinfo.price);
                          const disc = formatPrice(pinfo.price_after_discount);
                          return (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                              {disc ? (
                                <>
                                  <Typography 
                                    variant="h5" 
                                    sx={{
                                      fontWeight: "800",
                                      background: "linear-gradient(135deg, #d4af37 0%, #f4e5c2 100%)",
                                      backgroundClip: "text",
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent"
                                    }}
                                  >
                                    {disc} ج.م
                                  </Typography>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      textDecoration: "line-through",
                                      color: "text.secondary",
                                      fontWeight: 500
                                    }}
                                  >
                                    {orig} ج.م
                                  </Typography>
                                </>
                              ) : (
                                <Typography 
                                  variant="h5" 
                                  sx={{
                                    fontWeight: "800",
                                    color: "secondary.main"
                                  }}
                                >
                                  {orig} ج.م
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </Box>

                      {/* Sales Badge */}
                      <Chip
                        label={product.order_count ? `${lang === "ar" ? "تم بيع" : "Sold"} ${product.order_count}` : (lang === "ar" ? "شائع" : "Popular")}
                        size="small"
                        sx={{
                          mb: 2,
                          background: "linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)",
                          color: "#fff",
                          fontWeight: "700",
                          fontSize: "0.8rem",
                          px: 1.5,
                          boxShadow: "0 2px 8px rgba(46, 204, 113, 0.3)"
                        }}
                      />

                      {/* Rating */}
                      {product.reviews_summary ? (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                          <Rating 
                            value={parseFloat(product.reviews_summary.average_rating) || 0} 
                            readOnly 
                            size="small"
                            precision={0.5}
                            sx={{
                              "& .MuiRating-iconFilled": {
                                color: "#ffd700"
                              }
                            }}
                          />
                          <Typography variant="body2" color="text.secondary" fontWeight="600">
                            ({product.reviews_summary.total_reviews || 0})
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                          {t("no_rating", { defaultValue: lang === "ar" ? "بدون تقييم" : "No rating" })}
                        </Typography>
                      )}
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Empty State */}
        {products.length === 0 && (
          <Box 
            sx={{ 
              textAlign: "center", 
              py: 8,
              px: 2
            }}
          >
            <Typography 
              variant="h5" 
              color="text.secondary"
              fontWeight="600"
            >
              {lang === "ar" ? "لا توجد منتجات متاحة حالياً" : "No products available"}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default BestSellersPage;
